import React, { useEffect, useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Paper, Typography, Button, Grid, CircularProgress } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Plan } from '../../Models/Subscriptions';
import { baseEndpoint } from '../../App';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            margin: "15px",
            textAlign: "left",
            paddingBottom: theme.spacing(4),
            marginBottom: theme.spacing(3)
        },
        headerBar: {
            backgroundColor: "#0404fc",
            borderWidth: "5px",
            height: "29px",
            marginRight: "17px",
            marginLeft: "17px",
        },
        description: {
            margin: theme.spacing(2),
            textAlign: 'center'
        },
        plansContainer: {
            padding: theme.spacing(2),
            paddingTop: theme.spacing(1)
        },
        planCard: {
            padding: theme.spacing(2),
            margin: theme.spacing(1),
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        },
        titleSpacing: {
            marginBottom: theme.spacing(1) // New style for title spacing
        },
        chooseButton: {
            backgroundColor: "#17d703",
            color: "white",
            marginTop: theme.spacing(1), // Reduced from 2
            '&:hover': {
                backgroundColor: "#15c003"
            }
        },
        priceContainer: {
            marginBottom: theme.spacing(2)
        },
        price: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: theme.palette.primary.main
        },
        priceDetail: {
            color: theme.palette.text.secondary,
            fontSize: '0.9rem',
            marginBottom: theme.spacing(1)
        },
        minimumFee: {
            color: theme.palette.text.secondary,
            fontSize: '0.8rem',
            fontStyle: 'italic',
            marginTop: theme.spacing(1)
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
        },
        errorMessage: {
            color: theme.palette.error.main,
            textAlign: 'center',
            margin: theme.spacing(2)
        }

    })
);


export default function LandlordSubscriptions() {
    const history = useHistory();
    const classes = useStyles();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() =>{
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await fetch(baseEndpoint + 'subscription/plans/landlord');
            if (!response.ok) {
                throw new Error('Failed to fetch plans');
            }
            const data = await response.json();
            setPlans(data);
            setError(null);
        } catch (err) {
            setError('Failed to load subscription plans. Please try again later.');
            console.error('Error fetching plans:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChoosePlan = (plan: Plan) => {
        history.push(`/signup/landlord/register?plan=${plan.catalogId}&name=${encodeURIComponent(plan.title)}`);
    };

    if (loading) {
        return(
            <div className={classes.loadingContainer}>
                <CircularProgress />
            </div>
        )
    }

    if (error) {
        return (
            <div className={classes.errorMessage}>
                <Typography variant="h6">{error}</Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={fetchPlans}
                    style={{ marginTop: '16px' }}
                >
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div>
            <div className={classes.headerBar} />
            <Paper className={classes.root}>
                <div className={classes.description}>
                    <Typography variant="h4" className={classes.titleSpacing}>
                        Enhanced Score Plans
                    </Typography>
                    <Typography variant="body1" paragraph style={{ marginBottom: 8 }}>
                        Verify as little or as much information as you like. Every piece of verified
                        information you supply about your property will be added to the Parslii
                        Property Report and augment your Parslii Score, making your property stand
                        out to potential tenants.
                    </Typography>
                    <Typography variant="h6" className={classes.titleSpacing}>
                        Choose a plan that best fits your property portfolio and renting needs.
                    </Typography>
                </div>

                <Typography variant="h4" align="center" style={{ marginBottom: 16 }}>
                    Choose a Landlord Plan
                </Typography>

                <Grid container className={classes.plansContainer}>
                    {plans.map((plan, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Paper elevation={3} className={classes.planCard}>
                                <div>
                                    <Typography variant="h6" gutterBottom>
                                        {plan.title}
                                    </Typography>
                                    <div className={classes.priceContainer}>
                                        <Typography className={classes.price}>
                                            {plan.price}
                                        </Typography>
                                        <Typography className={classes.priceDetail}>
                                            {plan.priceDetail}
                                        </Typography>
                                    </div>
                                    <Typography variant="body1">
                                        {plan.description}
                                    </Typography>
                                    <Typography className={classes.minimumFee}>
                                        {plan.minimumFee}
                                    </Typography>
                                </div>
                                <Button
                                    variant="contained"
                                    className={classes.chooseButton}
                                    fullWidth
                                    onClick={() => handleChoosePlan(plan)}
                                >
                                    Choose This Plan
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </div>
    );
}